import appConfig from 'appConfig';
import { getState } from 'pluginServices/store';
import { selectCurrentMe } from 'data/CurrentUser/selectors';
import AssetResolver from 'resolvers/RTMResolvers/AssetResolver';
import { publishEvent } from 'services/EventService';
import { UPLOAD_STATUS } from 'pluginServices/constants/userActions';

import { loadSession } from '../data/CurrentUser/sessionManager';
import { visibility } from './constants';

/**
 * Creates an service that connects to the Mercury Service's asset
 * APIs.
 *
 * TODO: Move this out of a singleton.  If it really needs to persist,
 * either attach it something relevant, or create a context.
 */
export default (() => {
  let assetResolver;
  let isRegistered = false;

  // TODO: The cache wraps every object in an array, so that your get
  // return will never === your set parameter. Will deal with that later.
  const cache = {};
  const URN_EXTERNAL = 'urn:external';
  const UPLOAD_STATES = {
    IN_PROGRESS: 'currently uploading',
    IDLE: 'nothing being done',
  };

  const register = (connectionProvider) => {
    isRegistered = true;
    assetResolver = new AssetResolver(connectionProvider);
  };

  const _saveToCache = (asset) => {
    // the schema editor seems to rely on assets being stored only as asset.data.
    // I'm not sure if this is a good idea, since we would need the asset.type to
    // discriminate when we support more than one type of asset.
    cache[asset.urn] = { ...asset.source, ...asset };
  };

  // TODO: This might need a revisit.
  // The backend is returning bearerToken as a quick hack.  In the
  // future we will get a unique nonce.  Passing the bearerToken to the plugin
  // is a security risk.
  const _getNonce = () => loadSession();

  /**
   * The first step in uploading an asset is to provision it.  This is useful
   * when calling across the plugin API, as it will return the properties
   * needed to complete an upload.
   * @returns { { nonce: string, uploadURL: string } }
   */
  const provisionUpload = async () => {
    const nonce = await _getNonce();
    const uploadURL = appConfig.getAssetSaveURL();
    return { nonce, uploadURL };
  };

  const _provisionAssetData = (file) => {
    const { subscriptionId } = selectCurrentMe(getState());
    const assetData = new FormData();
    assetData.append('file', file);
    assetData.append('visibility', visibility.GLOBAL);
    assetData.append('subscriptionId', subscriptionId);
    return assetData;
  };

  /**
   * Given a courseware id and type, upload an asset to the asset API.
   * @param {string} id
   * @param {string} coursewareType
   */
  const uploadFile = (id, coursewareType) => async (keyPath, file) => {
    publishEvent(UPLOAD_STATUS, { eventName: UPLOAD_STATUS, value: UPLOAD_STATES.IN_PROGRESS });
    const { nonce, uploadURL } = await provisionUpload();
    const uploadRequestBody = _provisionAssetData(file);
    const uploadResponse = await fetch(`${uploadURL}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${nonce}`,
      },
      body: uploadRequestBody,
    });

    const assetData = await uploadResponse.json();
    await informAssetUsage(id, coursewareType, assetData.urn);
    publishEvent(UPLOAD_STATUS, { eventName: UPLOAD_STATUS, value: UPLOAD_STATES.IDLE });

    _saveToCache(assetData);
    return assetData.urn;
  };

  const addExternalAsset = (id, coursewareType) => async (url, mediaType, metadata) => {
    const assetData = await assetResolver.authorAssetCreate(url, mediaType, metadata);
    await informAssetUsage(id, coursewareType, assetData.urn);

    _saveToCache(assetData);
    return assetData.urn;
  };

  const authorAssetAlfrescoSelect = async (alfrescoImageId, hasChild, id, coursewareType) => {
    const assetData = await assetResolver.authorAssetAlfrescoSelect(alfrescoImageId, hasChild);
    if (assetData?.asset) {
      await informAssetUsage(id, coursewareType, assetData.asset.urn);
      if (assetData.asset?.metadata?.scaleMarker) {
        const scaleMarkerValue = assetData.asset.metadata.scaleMarker;  // backend gives us a string like "[urn123]" - ideally, it would have been "['urn1', 'urn2', 'urn3']"
        // Convert string array like "[urn123, urn234]" to actual array
        const match = scaleMarkerValue.match(/\[(.*?)\]/);
        let scaleMarkers = match ? match[1].split(',') : [];
        await Promise.all(
          scaleMarkers.map((markerUrn) => {
            const trimmedMarkerUrn = markerUrn.trim();
            return informAssetUsage(id, coursewareType, trimmedMarkerUrn);
          })
        );
      }
      _saveToCache(assetData.asset);
      return assetData.asset.urn;
    }
    return null;
  };

  const authorAlfrescoAssetsPushCount = async (courseId) => {
    const response = await assetResolver.authorAlfrescoAssetsPushCount(courseId);
    if (response) {
      return { pushAssetCount: response.pushAssetCount, totalImages: response.totalImages };
    }
    return null;
  };

  const authorAlfrescoAssetsPush = async (courseId, alfrescoNodeId) => {
    const response = await assetResolver.authorAlfrescoAssetsPush(courseId, alfrescoNodeId);
    if (response) {
      return response;
    }
    return null;
  };

  const authorAlfrescoAssetsProgress = async (courseId, syncType) => {
    const response = await assetResolver.authorAlfrescoAssetsProgress(courseId, syncType);
    if (response) {
      return response;
    }
    return null;
  };

  const addResolvedToCache = ([...assets]) => {
    assets.forEach((asset) => _saveToCache(asset));
  };

  const informAssetUsage = async (id, coursewareType, urn) => {
    await assetResolver.authorCoursewareAssetAdd(id, coursewareType, urn);
  };

  const removeFile = (id, coursewareType) => async (urn) => {
    await assetResolver.authorCoursewareAssetRemove(id, coursewareType, urn);
    return '';
  };

  const resolveAsset = async (urn) => {
    if (!cache[urn] || urn.startsWith(URN_EXTERNAL)) {
      const assetFromNetwork = await assetResolver.authorAssetGet(urn);
      _saveToCache(assetFromNetwork);
    }

    const asset = cache[urn];
    return asset;
  };

  const getIconAssetList = async (iconLibraries) => {
    const assetList = await assetResolver.authorIconAssetList({ iconLibraries });
    return assetList;
  };

  const getUpdatedAssets = () => {
    Object.keys(cache).forEach(async (urn) => {
      const { asset } = cache[urn];
      if (asset.assetProvider === 'ALFRESCO') {
        const assetFromAlfresco = await assetResolver.authorAssetGet(urn);
        _saveToCache(assetFromAlfresco);
      }
    });
  };

  return {
    isRegistered: () => isRegistered,
    register,
    provisionUpload,
    uploadFile,
    addExternalAsset,
    removeFile,
    resolveAsset,
    getIconAssetList,
    informAssetUsage,
    addResolvedToCache,
    authorAssetAlfrescoSelect,
    authorAlfrescoAssetsPushCount,
    authorAlfrescoAssetsPush,
    authorAlfrescoAssetsProgress,
    getUpdatedAssets,
    getStored: () => Object.assign({}, cache),
  };
})();
